import React, { useEffect, useRef, useState } from "react";

export default function MusicPlayer({ src, thumbnail }) {
  const audioRef = useRef(null);
  const discRef = useRef(null);
  const canvasRef = useRef(null); // 🎵 WAVEFORM CANVAS
  const miniPlayerRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(0.85);

  // 🎛 Floating player toggle
  const [isMini, setIsMini] = useState(false);

  // 🎵 Web Audio Analyzer nodes
  const [audioContext, setAudioContext] = useState(null);
  const [analyser, setAnalyser] = useState(null);

  // 🟢 AUTOPLAY FIX (mobile + desktop)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const tryPlay = () => {
      audio.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {});
    };

    tryPlay();

    const unlock = () => {
      tryPlay();
      document.removeEventListener("touchstart", unlock);
      document.removeEventListener("click", unlock);
    };

    document.addEventListener("touchstart", unlock, { once: true });
    document.addEventListener("click", unlock, { once: true });
  }, [src]);

  // 🔊 Sync volume
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // 🎵 PROGRESS updates
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const update = () =>
      setProgress((audio.currentTime / audio.duration) * 100 || 0);

    audio.addEventListener("timeupdate", update);
    return () => audio.removeEventListener("timeupdate", update);
  }, []);

  // 🎵 Initialize Analyzer + Waveform
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const srcNode = ctx.createMediaElementSource(audio);
    const anal = ctx.createAnalyser();

    anal.fftSize = 1024;
    srcNode.connect(anal);
    anal.connect(ctx.destination);

    setAudioContext(ctx);
    setAnalyser(anal);
  }, [src]);

  // 🎨 Waveform Animation
  useEffect(() => {
    if (!analyser || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const render = () => {
      requestAnimationFrame(render);

      analyser.getByteTimeDomainData(dataArray);

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#fff";

      ctx.beginPath();
      const slice = canvas.width / dataArray.length;

      dataArray.forEach((value, i) => {
        const y = (value / 255) * canvas.height;
        const x = i * slice;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });

      ctx.stroke();
    };

    render();
  }, [analyser]);

  // ▶️ PLAY / PAUSE
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => setIsPlaying(true));
    }
  };

  const onSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const value = Number(e.target.value);
    setProgress(value);
    audio.currentTime = (value / 100) * audio.duration;
  };

  // 🎛 Scroll → switch to mini mode
  useEffect(() => {
    const handleScroll = () => {
      const rect = miniPlayerRef.current?.getBoundingClientRect();
      if (!rect) return;

      if (rect.top < 0) setIsMini(true);
      else setIsMini(false);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div
      ref={miniPlayerRef}
      className={`music-player-container ${isMini ? "mini" : ""}`}
    >
      <audio ref={audioRef} src={src} preload="auto" />

      {/* 🎵 Waveform */}
      <canvas ref={canvasRef} className="wave-canvas"></canvas>

      <div className="music-ui">
        {/* 🎨 Rotating Disc */}
        <div
          ref={discRef}
          className={`disc ${isPlaying ? "spin" : ""}`}
          style={{ backgroundImage: `url('${thumbnail}')` }}
          onClick={togglePlay}
        />

        {/* ▶️ Play Button */}
        <button className="play-btn" onClick={togglePlay}>
          {isPlaying ? "⏸" : "▶️"}
        </button>

        {/* 📈 Progress */}
        <input
          type="range"
          className="progress-bar"
          value={progress}
          min="0"
          max="100"
          onChange={onSeek}
        />

        {/* 🔊 Volume */}
        <input
          type="range"
          className="volume-slider"
          min="0"
          max="1"
          step="0.01"
          value={volume}
          onChange={(e) => setVolume(Number(e.target.value))}
        />
      </div>

      {/* 📱 mini-floating mode tap-to-expand */}
      {isMini && (
        <button className="expand-btn" onClick={() => setIsMini(false)}>
          ⬆️
        </button>
      )}
    </div>
  );
}
